
import React, { useState } from 'react';
import { useSignalData } from '../context/SignalContext';
import { 
    DollarSign, RefreshCw, ShoppingBag, Percent, 
    Search, Filter, Calendar, Users, List, Download
} from 'lucide-react';
import { TransactionStatus } from '../types';

export const AffiliateReports: React.FC = () => {
    const { stats, transactions, currentUser, users } = useSignalData();
    const [searchTerm, setSearchTerm] = useState('');

    // Filter transactions for current affiliate
    const myTxns = transactions.filter(t => t.affiliateId === currentUser?.id);
    
    // Filter customers (F1)
    const myCustomers = users.filter(u => u.referredBy === currentUser?.id);
    
    // Search Logic
    const filteredTxns = myTxns.filter(t => 
        t.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || 
        t.product.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredCustomers = myCustomers.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8">
            {/* Header Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-800 mb-6 uppercase tracking-wide border-b border-slate-100 pb-4">
                    Thống kê hiệu quả
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                             <DollarSign className="w-6 h-6"/>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-slate-800">${stats.totalRevenue.toLocaleString()}</h3>
                            <p className="text-xs text-slate-500 font-medium uppercase">Tổng doanh số</p>
                        </div>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center shrink-0">
                             <RefreshCw className="w-6 h-6"/>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-slate-800">{stats.sales}</h3>
                            <p className="text-xs text-slate-500 font-medium uppercase">Đơn hàng phát sinh</p>
                        </div>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center shrink-0">
                             <ShoppingBag className="w-6 h-6"/>
                        </div>
                        <div>
                             <h3 className="text-2xl font-bold text-slate-800">{myTxns.filter(t => t.status === TransactionStatus.PAID || t.status === TransactionStatus.APPROVED).length}</h3>
                            <p className="text-xs text-slate-500 font-medium uppercase">Đơn hoàn tất</p>
                        </div>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center shrink-0">
                             <Percent className="w-6 h-6"/>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-slate-800">{stats.conversionRate}%</h3>
                            <p className="text-xs text-slate-500 font-medium uppercase">Tỷ lệ chuyển đổi</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Orders Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 bg-slate-50/50">
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <List className="w-5 h-5 text-brand-600"/> CHI TIẾT ĐƠN HÀNG
                    </h3>
                    <div className="flex gap-2 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input 
                                type="text" 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Tìm tên khách, gói dịch vụ..." 
                                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none"
                            />
                        </div>
                         <button className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 flex items-center gap-2 text-sm font-medium">
                            <Download className="w-4 h-4"/> Xuất Excel
                        </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-600">
                        <thead className="bg-slate-50 text-slate-700 font-bold border-b border-slate-200 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Mã đơn</th>
                                <th className="px-6 py-4">Tên khách hàng</th>
                                <th className="px-6 py-4">Thời gian</th>
                                <th className="px-6 py-4">Dịch vụ</th>
                                <th className="px-6 py-4 text-right">Doanh số</th>
                                <th className="px-6 py-4 text-right">Hoa hồng</th>
                                <th className="px-6 py-4 text-center">Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredTxns.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-400">Không có đơn hàng nào khớp với tìm kiếm.</td>
                                </tr>
                            ) : filteredTxns.map((txn) => (
                                <tr key={txn.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 font-mono text-xs text-slate-500">#{txn.id}</td>
                                    <td className="px-6 py-4 font-bold text-slate-800">{txn.customerName}</td>
                                    <td className="px-6 py-4 text-xs">{txn.date}</td>
                                    <td className="px-6 py-4">
                                        <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium border border-blue-100">{txn.product}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right font-medium">${txn.amount}</td>
                                    <td className="px-6 py-4 text-right font-bold text-emerald-600">+${txn.commission}</td>
                                    <td className="px-6 py-4 text-center">
                                         <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${
                                            txn.status === 'PAID' ? 'bg-green-100 text-green-700 border-green-200' : 
                                            txn.status === 'APPROVED' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                            txn.status === 'PENDING' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                            'bg-red-100 text-red-700 border-red-200'
                                        }`}>
                                            {txn.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Customers Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200 bg-slate-50/50">
                     <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <Users className="w-5 h-5 text-brand-600"/> DANH SÁCH KHÁCH HÀNG TRỌN ĐỜI
                    </h3>
                    <p className="text-slate-500 text-xs mt-1">Danh sách khách hàng đăng ký qua link của bạn. Bạn sẽ nhận hoa hồng trọn đời từ các khách hàng này.</p>
                </div>
                 <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-600">
                        <tbody className="divide-y divide-slate-100">
                            {filteredCustomers.length === 0 ? (
                                <tr><td className="px-6 py-8 text-center text-slate-400">Chưa có khách hàng nào.</td></tr>
                            ) : filteredCustomers.map(cus => (
                                <tr key={cus.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center font-bold text-slate-500">
                                                {cus.name.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800">{cus.name}</p>
                                                <p className="text-xs text-slate-400">{cus.email} • {cus.phone || 'No Phone'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <p className="text-xs text-slate-500">Ngày tham gia</p>
                                        <p className="font-medium text-slate-800">{cus.registerDate ? new Date(cus.registerDate).toLocaleDateString('vi-VN') : 'N/A'}</p>
                                    </td>
                                     <td className="px-6 py-4 text-right">
                                        <p className="text-xs text-slate-500">Tổng chi tiêu</p>
                                        <p className="font-bold text-brand-600">$0</p>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                         <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold border border-green-200">
                                            Active
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
            </div>
        </div>
    );
};
